import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { PRIMARYCOLOR } from '../../Constants';

function formatCountdown(totalSeconds) {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export default function MfaVerificationForm({
  code,
  emailHint,
  error,
  expiresIn,
  resendIn,
  verifying,
  resending,
  onCodeChange,
  onVerify,
  onResend,
  onBack,
}) {
  const expired = expiresIn <= 0;
  const busy = verifying || resending;

  const handleCodeChange = (event) => {
    const normalized = event.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 6);
    onCodeChange(normalized);
  };

  return (
    <Box component="form" onSubmit={onVerify}>
      <Stack spacing={2.25}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2.5,
              color: PRIMARYCOLOR,
              backgroundColor: alpha(PRIMARYCOLOR, 0.08),
              border: `1px solid ${alpha(PRIMARYCOLOR, 0.16)}`,
            }}
          >
            <VerifiedUserOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#0e2b5c', fontWeight: 850 }}>
              Verify your identity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the code sent to {emailHint || 'your email address'}.
            </Typography>
          </Box>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <TextField
          autoFocus
          fullWidth
          label="Verification code"
          value={code}
          onChange={handleCodeChange}
          disabled={busy || expired}
          autoComplete="one-time-code"
          placeholder="7K4M9Q"
          inputProps={{
            maxLength: 6,
            inputMode: 'text',
            'aria-label': 'Six-character verification code',
          }}
          sx={{
            '& input': {
              py: 1.65,
              textAlign: 'center',
              fontSize: '1.45rem',
              fontWeight: 850,
              letterSpacing: '0.42em',
              color: '#0e2b5c',
            },
          }}
        />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            backgroundColor: alpha(PRIMARYCOLOR, 0.045),
            border: `1px solid ${alpha(PRIMARYCOLOR, 0.1)}`,
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AccessTimeRoundedIcon sx={{ color: expiresIn <= 60 ? 'error.main' : PRIMARYCOLOR, fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 750 }} aria-live="polite">
              Code expires in {formatCountdown(expiresIn)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <LockOutlinedIcon sx={{ color: 'text.secondary', fontSize: 17 }} />
            <Typography variant="caption" color="text.secondary">
              Secure email verification
            </Typography>
          </Stack>
        </Stack>

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={code.length !== 6 || busy || expired}
          sx={{ minHeight: 46, borderRadius: 2, fontWeight: 800, backgroundColor: PRIMARYCOLOR }}
        >
          {verifying ? <CircularProgress size={21} color="inherit" /> : 'Verify and continue'}
        </Button>

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Button
            size="small"
            startIcon={resending ? <CircularProgress size={15} /> : <RefreshRoundedIcon />}
            onClick={onResend}
            disabled={busy || expired || resendIn > 0}
            sx={{ textTransform: 'none', fontWeight: 750 }}
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
          </Button>
          <Link
            component="button"
            type="button"
            onClick={onBack}
            disabled={busy}
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 17 }} />
            Back to login
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
}
